import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import { Plus, Search, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";

const statuses = ["new", "contacted", "qualified", "placed"];
const statusColors = {
  new: "#3B82F6",
  contacted: "#F59E0B",
  qualified: "#8B5CF6",
  placed: "#10B981",
};

const readAsDataURL = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function Candidates() {
  const { t } = useI18n();
  const [candidates, setCandidates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [draggingId, setDraggingId] = useState(null);

  const empty = {
    name: "", phone: "", email: "", country: "", experience: "",
    job_title: "", language_level: "", status: "new", tags: "", notes: "",
    photo_b64: "", cv_b64: "", cv_filename: "",
  };
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/candidates"); setCandidates(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...empty, ...c, tags: (c.tags || []).join(", ") });
    setOpen(true);
  };

  const handleFile = async (e, key, nameKey) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Max 2MB");
    const b64 = await readAsDataURL(f);
    setForm((s) => ({ ...s, [key]: b64, ...(nameKey ? { [nameKey]: f.name } : {}) }));
  };

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    const payload = {
      ...form,
      tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    if (editing) {
      await api.put(`/candidates/${editing.id}`, payload);
    } else {
      await api.post("/candidates", payload);
    }
    toast.success("OK");
    setOpen(false);
    load();
  };

  const del = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/candidates/${id}`);
    load();
  };

  const onDrop = async (status) => {
    if (!draggingId) return;
    await api.patch(`/candidates/${draggingId}/status`, { status });
    setDraggingId(null);
    load();
  };

  const filtered = candidates.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [c.name, c.email, c.phone, c.country, c.job_title].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div className="space-y-6" data-testid="candidates-root">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">CRM</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{t("candidates")}</h1>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
            <input data-testid="cand-search" placeholder="Search…" className="pl-8 pr-3 py-2 border border-zinc-300 text-sm w-56" style={{borderRadius: 2}} value={search} onChange={(e) => setSearch(e.target.value)}/>
          </div>
          <button data-testid="open-new-candidate" onClick={openNew} className="btn-primary inline-flex items-center gap-2"><Plus size={16}/> {t("add_candidate")}</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="kanban-board">
        {statuses.map((s) => {
          const items = filtered.filter((c) => c.status === s);
          return (
            <div
              key={s}
              className="kanban-col p-3"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(s)}
              data-testid={`col-${s}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{background: statusColors[s]}}></span>
                  <span className="text-xs uppercase tracking-[0.15em] font-bold">{t(s)}</span>
                </div>
                <span className="text-xs text-zinc-500">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map((c) => (
                  <div
                    key={c.id}
                    draggable
                    onDragStart={() => setDraggingId(c.id)}
                    onClick={() => openEdit(c)}
                    data-testid={`cand-${c.id}`}
                    className="bg-white border border-zinc-200 p-3 cursor-pointer hover:-translate-y-0.5 transition-transform shadow-sm"
                    style={{ borderRadius: 2 }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-zinc-100 flex items-center justify-center font-bold text-xs overflow-hidden" style={{borderRadius: 2}}>
                        {c.photo_b64 ? <img src={c.photo_b64} alt="" className="w-full h-full object-cover"/> : (c.name?.[0]?.toUpperCase() || "?")}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{c.name}</div>
                        <div className="text-xs text-zinc-500 truncate">{c.country || "—"}</div>
                      </div>
                    </div>
                    <div className="text-xs text-zinc-600 truncate">{c.job_title}</div>
                    {c.tags?.length > 0 && (
                      <div className="flex gap-1 flex-wrap mt-2">
                        {c.tags.slice(0, 3).map((tg, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 bg-zinc-100" style={{borderRadius: 2}}>{tg}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {items.length === 0 && <div className="text-xs text-zinc-400 text-center py-6">—</div>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? t("edit") : t("add_candidate")}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto p-1">
            <input data-testid="cand-name" placeholder={t("name")} className="border border-zinc-300 px-3 py-2 md:col-span-2" style={{borderRadius: 2}} value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}/>
            <input data-testid="cand-email" placeholder={t("email")} className="border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}/>
            <input data-testid="cand-phone" placeholder={t("phone")} className="border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}/>
            <input data-testid="cand-country" placeholder={t("country")} className="border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.country} onChange={(e) => setForm({...form, country: e.target.value})}/>
            <input data-testid="cand-job" placeholder={t("job_title")} className="border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.job_title} onChange={(e) => setForm({...form, job_title: e.target.value})}/>
            <input data-testid="cand-exp" placeholder={t("experience")} className="border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.experience} onChange={(e) => setForm({...form, experience: e.target.value})}/>
            <input data-testid="cand-lang" placeholder={t("language_level")} className="border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.language_level} onChange={(e) => setForm({...form, language_level: e.target.value})}/>
            <select className="border border-zinc-300 px-3 py-2 bg-white" style={{borderRadius: 2}} value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
              {statuses.map((s) => <option key={s} value={s}>{t(s)}</option>)}
            </select>
            <input placeholder={t("tags_csv")} className="border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})}/>
            <textarea placeholder={t("notes")} rows={3} className="border border-zinc-300 px-3 py-2 md:col-span-2" style={{borderRadius: 2}} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("photo")}</label>
                <input type="file" accept="image/*" onChange={(e) => handleFile(e, "photo_b64")} className="mt-1 block w-full text-sm" data-testid="cand-photo"/>
                {form.photo_b64 && <img src={form.photo_b64} alt="" className="mt-2 w-20 h-20 object-cover border border-zinc-200" style={{borderRadius: 2}}/>}
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("cv")}</label>
                <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => handleFile(e, "cv_b64", "cv_filename")} className="mt-1 block w-full text-sm" data-testid="cand-cv"/>
                {form.cv_filename && <div className="mt-2 text-xs text-zinc-600">{form.cv_filename}</div>}
              </div>
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            {editing && <button onClick={() => { del(editing.id); setOpen(false); }} data-testid="cand-delete" className="text-[#E63946] text-sm font-medium">{t("delete")}</button>}
            <div className="flex gap-2 ml-auto">
              <button onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300">{t("cancel")}</button>
              <button data-testid="cand-save" onClick={save} className="btn-primary">{t("save")}</button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
